import type { TaskArtifactView } from '@/lib/api/generated/model';

type Artifact = Pick<
  TaskArtifactView,
  'kind' | 'forge_host' | 'repository' | 'web_url'
>;

interface ProjectAddress {
  base: string;
  style: 'gitlab' | 'github';
}

/** `https://host/group/repo` and the forge's style, from an MR or PR address. */
function fromRequestUrl(url: string | null): ProjectAddress | null {
  if (!url) return null;
  const gitlab = /^(https?:\/\/[^?#]+?)\/-\/merge_requests\/\d+/.exec(url);
  if (gitlab) return { base: gitlab[1], style: 'gitlab' };
  const github = /^(https?:\/\/[^?#]+?)\/pull\/\d+/.exec(url);
  if (github) return { base: github[1], style: 'github' };
  return null;
}

const keyOf = (artifact: Artifact) =>
  `${artifact.forge_host.toLowerCase()}/${artifact.repository}`;

/**
 * Web addresses for a repository and its branches, which the record does not
 * carry (only merge and pull requests have a `web_url`).
 *
 * Read off a merge or pull request the task created in the same repository
 * when there is one, since that address shows both where the project lives
 * and which forge it is (`/-/merge_requests/` is GitLab, `/pull/` is GitHub).
 * Without one, the host decides: `github.com` is GitHub, any other host is
 * taken for GitLab, the forge this platform mostly runs against.
 */
export function forgeLinks(artifacts: readonly Artifact[]) {
  const known = new Map<string, ProjectAddress>();
  for (const artifact of artifacts) {
    const address = fromRequestUrl(artifact.web_url);
    if (address && !known.has(keyOf(artifact))) {
      known.set(keyOf(artifact), address);
    }
  }
  const addressOf = (artifact: Artifact): ProjectAddress | null => {
    const found = known.get(keyOf(artifact));
    if (found) return found;
    if (!artifact.forge_host || !artifact.repository) return null;
    const host = artifact.forge_host.toLowerCase();
    return {
      base: `https://${host}/${artifact.repository}`,
      style: host === 'github.com' ? 'github' : 'gitlab',
    };
  };
  return {
    repository: (artifact: Artifact) => addressOf(artifact)?.base ?? null,
    branch: (artifact: Artifact, branch: string | null) => {
      const address = addressOf(artifact);
      if (!address || !branch) return null;
      const path = branch.split('/').map(encodeURIComponent).join('/');
      return address.style === 'gitlab'
        ? `${address.base}/-/tree/${path}`
        : `${address.base}/tree/${path}`;
    },
  };
}

export type ForgeLinks = ReturnType<typeof forgeLinks>;
